<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <div class="d-flex justify-content-between w-100" v-if="!isLoading">
        <h6 class="mb-0">{{ $t('sources.source_details') }}</h6>
        <img class="tooltip-icon icon-small ml-3" id="tooltip-source-show" v-bind:src="'/app/images/icons/info.svg'" />
        <b-popover target="tooltip-source-show" variant="warning" triggers="hover" placement="bottom">
          <div v-html="$t('tooltips.source_show')"></div>
        </b-popover>
      </div>
    </template>
    <template slot="dashboard-body">
      <div class="dashboard-content products-container" v-if="!isLoading">
        <div v-if="products.length > 0">
          <div class="d-flex justify-content-between my-4">
            <div class="d-flex align-items-center mb-4">
              <img class="icon" v-bind:src="'/app/images/icons/sources/'+source.config.fileType+'.svg'" />
              <h5>{{ source.name }}</h5>
            </div>
            <div class="d-flex align-items-center">
              <img class="icon" src="/app/images/icons/success.svg" />
              <div>
                <p class="text-orange">{{ source.lastProductCount }} {{$t('products.products2')}}</p>
                <p>{{ countHoursAgo(source.lastUpdate, new Date()) }} {{ $t('global.hours_ago') }}</p>
              </div>
            </div>
          </div>
          <h6>{{ $t('products.displayed_data') }}</h6>
          <multiselect
            class="source-variables-multiselect"
            group-label="name"
            group-values="properties"
            label="propertyLabel"
            track-by="propertyName"
            v-model="selectedProductProperties"
            :allow-empty="false"
            :close-on-select="false"
            :deselect-label="''"
            :deselect-group-label="$t('products.deselect_group_label')"
            :group-select="true"
            :multiple="true"
            :options="productSources"
            :placeholder="$t('feeds.choose_variable')"
            :searchable="true"
            :select-label="''"
            :select-group-label="$t('products.select_group_label')"
            :selected-label="''">
            <template slot="tag" slot-scope="{ option, remove }">
              <span class="multiselect__tag">
                <span>{{ option.propertyName }}</span>
                <i class="multiselect__tag-icon" @click="remove(option)" aria-hidden="true" tabindex="1"></i>
              </span>
            </template>
            <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
          </multiselect>
          <div class="d-flex align-items-center justify-content-between my-4 pb-4" id="mainProductIdSelect">
            <div class="d-flex align-items-center">
              <strong class="mr-4">{{$t('sources.main_id')}}</strong>
              <multiselect
                label="propertyLabel"
                v-model="selectedProductProperty"
                :allow-empty="true"
                :deselect-label="''"
                :options="productSources[0].properties"
                :placeholder="$t('feeds.choose_variable')"
                :searchable="true"
                :select-label="''"
                :selected-label="''">
                <template slot="option" slot-scope="props">
                  <span class="option__title">{{ props.option.propertyLabel }}</span>
                </template>
                <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
              </multiselect>
            </div>
            <div class="d-flex">
              <button class="btn btn-small-medium btn-orange-dark mx-4" @click="updateMainProductId">
                <save-icon></save-icon>
                <span>{{$t('global.save')}}</span>
              </button>
              <router-link :to="{ name: 'source.edit', params: { projectId: this.$route.params.projectId, sourceId: this.$route.params.sourceId }}">
                <button class="btn btn-small-medium btn-gray-dark">
                  <arrow-left-circle-icon></arrow-left-circle-icon>
                  <span>{{ $t('global.back') }}</span>
                </button>
              </router-link>
            </div>
          </div>
          <products-pagination
            @onProductsPerPageChange="selectProductsPerPage"
            @onPaginateCallback="paginate"
            :page="paginationSettings.currentPage"
            :productsCount="productsCount"
            :productsPerPage="paginationSettings.perPage">
          </products-pagination>
          <div v-if="!isReloading">
            <table class="table table-striped table-bordered">
              <thead class="thead-light">
                <th v-for="(value, key) in selectedProductProperties" v-bind:key="key" @click="sortProducts(value.propertyName)">
                  <div class="d-flex justify-content-center align-items-center">
                    <div>{{ value.propertyLabel }}</div>
                    <div class="d-flex flex-column">
                      <chevron-up-icon></chevron-up-icon>
                      <chevron-down-icon></chevron-down-icon>
                    </div>
                  </div>
                </th>
              </thead>
              <tbody>
                <tr v-for="(product, index) in products" v-bind:key="index">
                  <td v-for="(product_value, product_key) in selectedProductProperties" v-bind:key="product_key">
                    <p class="break-text">{{ product[product_value.propertyName] }}</p>
                    </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="reloading-spinner">
            <div class="spinner-border text-orange" role="status"></div>
          </div>
        </div>
        <div v-else class="dashboard-content">
          <div class="alert alert-danger mb-4 mt-3" role="alert">
            {{ $t('products.products_retrieve_error_no_update') }}
          </div>
        </div>
      </div>
      <div v-else>
        <div class="spinner-border text-orange" role="status"></div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import DashboardTemplate from '@/views/Dashboard'
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import ProductsPagination from '../Products/Partials/ProductsPagination'
import products from '@/mixins/products'
import dates from '@/mixins/dates'
import { ArrowLeftCircleIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'vue-feather-icons'

export default {
  name: 'SourceShow',

  data () {
    return {
      isLoading: true,
      isReloading: false,
      paginationSettings: {
        currentPage: 1,
        perPage: 10
      },
      productsCount: 0,
      products: [],
      productSources: [],
      selectedProductProperties: [],
      selectedProductProperty: null,
      sortColumn: '',
      sortOrder: 'asc',
      source: {},
      sources: []
    }
  },

  mixins: [dates, products],

  created () {
    this.getProjectSources()
  },

  methods: {
    getProjectSources () {
      this.isLoading = true
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/data/sources/' + this.$route.params.projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.sources = response.data
            this.source = this.sources.filter(source => source.id === Number(this.$route.params.sourceId))[0]
            this.getSourceProducts()
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    getSourceProducts () {
      if (this.products.length > 0) this.isReloading = true
      else this.isLoading = true
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/source/' + this.$route.params.projectId + '/' + this.$route.params.sourceId, {
        filterRules: [],
        orderBy: {
          field: this.sortColumn,
          sort: this.sortOrder
        },
        start: (this.paginationSettings.currentPage * this.paginationSettings.perPage) - this.paginationSettings.perPage,
        end: (this.paginationSettings.currentPage * this.paginationSettings.perPage)
      })
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.products = response.data.products
            this.productsCount = response.data.length
            this.isReloading = false
            if (this.selectedProductProperties.length === 0) this.preselectProductProperties()
            this.isLoading = false
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    selectProductsPerPage (value) {
      this.paginationSettings.perPage = value
      this.getSourceProducts()
    },

    paginate (value) {
      this.paginationSettings.currentPage = value
      this.getSourceProducts()
    },

    preselectProductProperties () {
      this.productSources = this.getProductSources(this.products[0])
      const productPropertiesCount = this.productSources[0].properties.length
      let endIndex = 0
      if (productPropertiesCount < 5) endIndex = productPropertiesCount
      else endIndex = 5
      for (let i = 0; i < endIndex; i++) {
        this.selectedProductProperties.push(this.productSources[0].properties[i])
      }
      this.selectedProductProperty = this.productSources[0].properties.filter(property => this.source.config.idPropertyName === property.propertyName)[0]
    },

    sortProducts (sortColumn) {
      if (this.sortOrder === 'asc') this.sortOrder = 'desc'
      else this.sortOrder = 'asc'
      this.sortColumn = sortColumn
      this.getSourceProducts()
    },

    updateMainProductId () {
      const sourceConfig = {
        name: this.source.name,
        active: this.source.active,
        description: '',
        config: this.source.config
      }
      sourceConfig.config.idPropertyName = this.selectedProductProperty.propertyName
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/sources/update/' + this.$route.params.projectId + '/' + this.source.id, sourceConfig)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.$router.push({ name: 'sources', params: { id: this.$route.params.projectId } })
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    }
  },

  components: { ArrowLeftCircleIcon, ChevronDownIcon, ChevronUpIcon, DashboardTemplate, Multiselect, ProductsPagination, SaveIcon }
}
</script>
